import { useEffect, useRef } from 'react'
import type { PluginListenerHandle } from '@capacitor/core'
import { SwipeBack, isIOS } from '../native/SwipeBack'

interface UseSwipeBackOptions {
  onBack: () => void
  enabled?: boolean
}

// Module-level stack of active swipe-back handlers. Multiple screens / drawers
// may be mounted simultaneously (e.g. Profile underneath an open
// ProfilePostModal). Each registers its handler when enabled, but only the
// most-recently-mounted handler — the one on top of the visual stack —
// should respond to a swipe. Without this, one swipe would fire every
// handler and pop the user past multiple layers at once.
type Handler = () => void
const handlerStack: Handler[] = []
let globalCommitListener: PluginListenerHandle | undefined
let globalListenerPending = false

async function ensureGlobalListener() {
  if (globalCommitListener || globalListenerPending) return
  globalListenerPending = true
  const handle = await SwipeBack.addListener('swipeBackCommit', () => {
    const top = handlerStack[handlerStack.length - 1]
    if (!top) return
    void SwipeBack.popSnapshot()
    top()
  })
  globalCommitListener = handle
  globalListenerPending = false
}

export function useSwipeBack({ onBack, enabled = true }: UseSwipeBackOptions) {
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    if (!enabled || !isIOS()) return

    const handler: Handler = () => onBackRef.current()
    handlerStack.push(handler)
    void ensureGlobalListener()
    void SwipeBack.enable()

    return () => {
      const idx = handlerStack.lastIndexOf(handler)
      if (idx !== -1) handlerStack.splice(idx, 1)
      if (handlerStack.length === 0) {
        void SwipeBack.disable()
      }
    }
  }, [enabled])
}
